import { AuiSupernav } from '@avalara/skylab-sdk/react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { isOneWebDomainRuntime } from '../../helpers';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { useFullBleedMain } from '../../hooks/use-full-bleed-main';
import { checkIsAuthenticated, getAccessToken } from '../../lib/helpers';
import { authRedirect } from '../../lib/side-effects';
import { fetchAccountData } from '../../lib/side-effects/accounts';
import { fetchNextBestProducts } from '../../lib/side-effects/next-best-product';
import { selectState } from '../../selectors';

// Shared between containers/product-catalog/index.tsx and containers/product-catalog/product-detail.tsx —
// both gate on the same feature flag/auth state, fetch the same account/next-best-product data, and
// render the same AvaApp nav override.
export const useProductCatalogShell = () => {
  const dispatch = useAppDispatch();
  const {
    account: { accountId, cam, camOwned, customerAccountId, obligations, user },
    data: { isLoading },
    features,
    nextBestProduct,
  } = useSelector(selectState);

  useEffect(() => {
    if (!features.SHOULDSHOWPRODUCTCATALOG) return;

    if (!checkIsAuthenticated()) {
      authRedirect();
      return;
    }

    dispatch(fetchAccountData());
  }, [features.SHOULDSHOWPRODUCTCATALOG]);

  useEffect(() => {
    if (!features.SHOULDSHOWPRODUCTCATALOG || !accountId) return;

    dispatch(fetchNextBestProducts());
  }, [features.SHOULDSHOWPRODUCTCATALOG, accountId]);

  useEffect(() => {
    if (!accountId || isLoading) return;
    if (!isOneWebDomainRuntime() || typeof window.AvaApp?.renderAuiNav !== 'function') return;

    window.AvaApp.renderAuiNav();

    return () => {
      if (typeof window.AvaApp?.removeAuiNav === 'function') {
        window.AvaApp.removeAuiNav();
      }
    };
  }, [accountId, isLoading]);

  const renderNavigation = () => {
    if (!accountId || isLoading) return null;

    return (
      <div className='product-catalog-nav'>
        <AuiSupernav accountId={accountId.toString()} requestCurrentAccessToken={getAccessToken} />
      </div>
    );
  };

  const shouldRender = Boolean(features.SHOULDSHOWPRODUCTCATALOG) && checkIsAuthenticated();

  // Both catalog pages paint the full-bleed nav strip, so they need the shell's <main> to contain
  // its 100vw overhang for as long as they're mounted.
  useFullBleedMain(shouldRender);

  return {
    dispatch,
    accountId,
    cam,
    camOwned,
    customerAccountId,
    obligations,
    user,
    isLoading,
    features,
    nextBestProduct,
    renderNavigation,
    shouldRender,
  };
};
